import confetti from 'canvas-confetti'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { AppDialog } from '@/components/AppDialog'
import { PartyIcon } from '@/components/icons/PartyIcon'
import { Button } from '@/components/ui/button'

const PRIMARK_CLICK_COLLECT_URL = 'https://www.primark.com/en-gb/click-and-collect'

const CONFETTI_COLORS = ['#fad600', '#1a9cd8', '#00aeff', '#19b5f0', '#333']

interface PrimarkLaunchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function runConfetti() {
  confetti({
    origin: { x: 0.5, y: 0.5 },
    colors: CONFETTI_COLORS,
    particleCount: 100,
    spread: 70,
  })
}

export function PrimarkLaunchDialog({ open, onOpenChange }: PrimarkLaunchDialogProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (open) {
      runConfetti()
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setExpanded(false)
    }
  }, [open])

  const cardClassName = 'bg-white rounded-[16px] p-6 shadow-[0px_2px_6px_0px_rgba(0,0,0,0.1)] flex flex-col items-center text-center'

  return (
    <AppDialog
      isOpen={open}
      onOpenChange={onOpenChange}
      title='Primark'
      titleClassName='sr-only'
      className='max-w-[90dvw] sm:max-w-lg'
      alwaysDialog
      dialogContentClassName='p-0'
    >
      <div className={cardClassName}>
        <PartyIcon />
        {!expanded ? (
          <>
            <div className='mt-4 flex flex-col gap-2'>
              <h2 className='text-text-primary text-lg leading-6 font-medium'>{t('primark.launchDialogTitle')}</h2>
              <p className='text-text-secondary text-base leading-5'>{t('primark.launchDialogSubtitle')}</p>
            </div>
            <button type='button' onClick={() => setExpanded(true)} className='text-text-link mt-4 text-sm leading-4 underline'>
              {t('primark.launchDialogCta')}
            </button>
          </>
        ) : (
          <>
            <div className='mt-4 flex flex-col gap-2'>
              <h2 className='text-text-primary text-lg leading-6 font-medium'>{t('primark.launchDialogTitle')}</h2>
              <p className='text-text-secondary text-base leading-5'>{t('primark.launchDialogSubtitle')}</p>
            </div>
            <div className='mt-4 flex w-full flex-col gap-4'>
              <div className='flex flex-col gap-2'>
                <span className='text-text-primary text-sm leading-4 font-medium'>1.</span>
                <p className='text-text-secondary text-center text-sm leading-4'>
                  <Trans
                    i18nKey='primark.launchDialogStep1'
                    components={{
                      Link: (
                        <a
                          href={PRIMARK_CLICK_COLLECT_URL}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-text-link underline'
                        />
                      ),
                    }}
                  />
                </p>
              </div>
              <div className='flex flex-col gap-2'>
                <span className='text-text-primary text-sm leading-4 font-medium'>2.</span>
                <p className='text-text-secondary text-center text-sm leading-4'>{t('primark.launchDialogStep2')}</p>
              </div>
              <div className='flex flex-col gap-2'>
                <span className='text-text-primary text-sm leading-4 font-medium'>3.</span>
                <p className='text-text-secondary text-center text-sm leading-4'>{t('primark.launchDialogStep3')}</p>
              </div>
            </div>
            <Button onClick={() => onOpenChange(false)} size='lg' className='mt-4 w-full'>
              {t('primark.howItWorksCloseButton')}
            </Button>
          </>
        )}
      </div>
    </AppDialog>
  )
}
